import React from "react";
import { formatPdfExportDate } from "../../../helper/common";

/**
 * Table calculation functions for PDF export
 */

/**
 * Get display value for a table cell
 */
const getDisplayValue = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) {
    return value.map((item) => getDisplayValue(item)).filter(Boolean).join(", ");
  }
  if (typeof value === "object") {
    if (value.label != null && String(value.label).trim() !== "") {
      return String(value.label);
    }
    const fileLikeName = value.name ?? value.AttachmentName ?? value.fileName;
    if (fileLikeName != null && String(fileLikeName).trim() !== "") {
      return String(fileLikeName);
    }
    if (value.value !== undefined && value.value !== null) {
      if (typeof value.value === "object") {
        return getDisplayValue(value.value);
      }
      const asString = String(value.value);
      return asString === "" || asString === "undefined" ? "" : asString;
    }
    return "";
  }
  return String(value);
};

/**
 * Display text for one cell (date columns use dd-MM-yyyy for export).
 */
const getCellExportDisplayText = (column, cellValue) => {
  if (column?.type === "date") {
    return formatPdfExportDate(cellValue);
  }
  return getDisplayValue(cellValue);
};

/**
 * Calculate sum for a column
 */
export const calculateSum = (data, columnIndex) => {
  if (!data || !Array.isArray(data)) return 0;
  return data.reduce((sum, row) => {
    const value = row[`col_${columnIndex}`];
    const numericValue = parseFloat(value);
    return isNaN(numericValue) ? sum : sum + numericValue;
  }, 0);
};

/**
 * Calculate average for a column
 */
export const calculateAverage = (data, columnIndex) => {
  if (!data || !Array.isArray(data)) return 0;
  const validValues = data.filter((row) => {
    const value = row[`col_${columnIndex}`];
    return !isNaN(parseFloat(value)) && value !== "" && value != null;
  });
  if (validValues.length === 0) return 0;
  const sum = calculateSum(data, columnIndex);
  return sum / validValues.length;
};

/**
 * Calculate count for a column
 */
export const calculateCount = (data, columnIndex) => {
  if (!data || !Array.isArray(data)) return 0;
  return data.filter((row) => {
    const value = row[`col_${columnIndex}`];
    return value !== "" && value != null;
  }).length;
};

/**
 * Get calculation function based on operation type
 */
export const getCalculationFunction = (operation) => {
  switch (operation) {
    case "addition":
      return calculateSum;
    case "average":
      return calculateAverage;
    case "count":
      return calculateCount;
    default:
      return calculateSum;
  }
};

/**
 * Get display name for operation
 */
export const getOperationDisplayName = (operation) => {
  switch (operation) {
    case "addition":
      return "Total";
    case "average":
      return "Average";
    case "count":
      return "Count";
    default:
      return "Total";
  }
};

/**
 * Render table with calculations (similar to TableRenderer.jsx)
 */
export const renderTableWithCalculations = (element) => {
  const { properties = {}, value } = element;
  const columns = properties?.columns || [];
  const tableData = value || [];

  if (columns.length === 0) {
    return (
      <div className="form-element table-element mb-3">
        <div className="form-field-value">No table data available</div>
      </div>
    );
  }

  const columnCalculations = {};
  columns.forEach((column, columnIndex) => {
    if (column.type === "number" && column.showResult) {
      const operation = column.operation || "addition";
      const calculateFn = getCalculationFunction(operation);
      const calculatedValue = calculateFn(tableData, columnIndex);

      columnCalculations[columnIndex] = {
        value: calculatedValue,
        operation,
        displayName: getOperationDisplayName(operation),
      };
    }
  });

  return (
    <div className="form-element table-element mb-3">
      {/* Main Table */}
      <table className="pdf-table w-100 mb-2">
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th key={index} className="pdf-table-header p-2 text-left">
                {column.title || `Column ${index + 1}`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableData.length > 0 ? (
            tableData.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {columns.map((column, columnIndex) => {
                  const cellValue = row[`col_${columnIndex}`] || "";
                  return (
                    <td key={columnIndex} className="pdf-table-cell p-2 text-left">
                      {getCellExportDisplayText(column, cellValue)}
                    </td>
                  );
                })}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className="pdf-table-cell text-center p-2">
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Calculation Results */}
      {Object.keys(columnCalculations).length > 0 && (
        <div className="table-calculations mt-3 d-flex flex-wrap">
          {Object.entries(columnCalculations).map(([columnIndex, calculation]) => (
            <div key={columnIndex} className="calculation-result-box d-flex flex-column mr-4">
              <div className="calculation-label mb-1">
                {calculation.displayName}
              </div>
              <div className="calculation-value">
                {calculation.value}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
